import { useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useModal } from "../../context/ModalProvider";
import { PreferencesModal } from "../modals/PreferencesModal";
import "./fg.css";

/**
 * In-app sub-navigation. Ports `FG.AppSubNav` from the design — same horizontal
 * tabs with active underline.
 *
 * v4 IA (journey §9): four nav tabs (Dashboard, My Training, My Fuel, Preferences).
 * "My Fueling Plans" (`/plans`) is the history list — routed but omitted from nav;
 * an individual plan (`/plan/:id`) is a detail view, not a tab. Preferences is a
 * modal — not a route — so its tab is a `<button>` that opens `<PreferencesModal>`
 * via `useModal()`, not a `<NavLink>`.
 *
 * The bar is full-bleed; the inner wrapper carries `fg-page-width` so the tabs
 * line up with each screen's content column.
 */
const ROUTE_ITEMS: { label: string; to: string }[] = [
  { label: "Dashboard", to: "/dashboard" },
  // "My Fueling Plans" (/plans) — history list; routed but not a nav tab (v4 IA).
  // Training Planner + My Activities merged into one surface (docs/my-training).
  { label: "My Training", to: "/my-training" },
  // My Fuel — the fuel journey's home (box timeline), sits alongside My Training
  // and My Fueling Plans (journeys IA split, lane A).
  { label: "My Fuel", to: "/my-fuel" }
];

const tabClass = (active: boolean): string =>
  `fgg-subnav__tab${active ? " fgg-subnav__tab--active" : ""}`;

export function AppSubNav() {
  const location = useLocation();
  const { open: openModal, close: closeModal } = useModal();

  const openPreferences = () => {
    const modalId = openModal(<PreferencesModal onClose={() => closeModal(modalId)} />);
  };

  // R4: when the tab row overflows, keep the active tab in view. `scrollIntoView`
  // with `inline:"nearest"` only scrolls the horizontal nav row (its nearest
  // scroll ancestor), never the page. Runs on mount and whenever the route
  // changes so the active underline is always reachable.
  const navRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const active = navRef.current?.querySelector<HTMLElement>(".fgg-subnav__tab--active");
    active?.scrollIntoView({ inline: "nearest", block: "nearest" });
  }, [location.pathname]);

  return (
    <div className="fgg-subnav">
      <div className="fgg-subnav__inner fg-page-width">
        {/* R4: primary tabs scroll horizontally (scrollbar hidden); Preferences
            is a SIBLING of the scroll row inside `__inner`, so it stays pinned
            and independently visible at every width — never clipped, never
            overlapping, never causing body h-scroll. */}
        <nav ref={navRef} className="fgg-subnav__nav" aria-label="Primary">
          {ROUTE_ITEMS.map((it) => {
            // Pending generation (/plan/pending/…) — no tab is active while building.
            const path = location.pathname;
            const suppressActive = path.startsWith("/plan/pending");
            const on =
              !suppressActive &&
              (path === it.to || path.startsWith(`${it.to}/`));
            return (
              <NavLink
                key={it.to}
                to={it.to}
                end
                className={tabClass(on)}
                aria-current={on ? "page" : undefined}>
                {it.label}
              </NavLink>
            );
          })}
        </nav>
        {/* Preferences is a modal, not a route — a `<button>` pinned outside the
            scroll row (flex-shrink:0). */}
        <button
          type="button"
          onClick={openPreferences}
          className={`${tabClass(false)} fgg-subnav__tab--end`}>
          Preferences
        </button>
      </div>
    </div>
  );
}
